/**
 * SEO-only helpers. Nothing here changes visible page content, routing or
 * product behavior — it only computes the metadata written into <head>.
 */

export const CANONICAL_ORIGIN = 'https://applyt.ai';
export const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/applyt-og.svg`;
export const DEFAULT_SEO_TITLE = 'Applyt';
export const DEFAULT_SEO_DESCRIPTION = "Don't just learn from experts, work like them";

export const INDEX_ROBOTS = 'index,follow';
export const NOINDEX_ROBOTS = 'noindex,follow';

/** Only these static surfaces are SEO acquisition pages in this phase. */
export const INDEXABLE_STATIC_PATHS = [
  '/',
  '/lenses',
  '/guides/strategy-to-execution',
  '/guides/business-systems-without-owner',
  '/guides/predictable-sales-process',
  '/plugins/pricing-power-advisor',
] as const;

/** A Lens page is indexable only when the Lens is published (see Lenses visibility rules). */
export const PUBLISHED_LENS_STATUS = 'published';

export type RobotsDirective = typeof INDEX_ROBOTS | typeof NOINDEX_ROBOTS;

/** Strip origin, query string, hash and trailing slashes (root stays "/"). */
export function normalizeSeoPath(pathOrUrl: string): string {
  let path = typeof pathOrUrl === 'string' ? pathOrUrl.trim() : '';
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      path = '/';
    }
  }
  path = path.split('#')[0].split('?')[0];
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.length > 1) path = path.replace(/\/+$/, '');
  return path === '' ? '/' : path;
}

/** Canonical URL always uses the production host, never the current browser origin. */
export function buildCanonicalUrl(pathOrUrl: string): string {
  return `${CANONICAL_ORIGIN}${normalizeSeoPath(pathOrUrl)}`;
}

export function isLensProfilePath(pathOrUrl: string): boolean {
  const match = /^\/lens\/([^/]+)$/.exec(normalizeSeoPath(pathOrUrl));
  return Boolean(match && match[1].length > 0);
}

export function isDefaultIndexablePath(pathOrUrl: string): boolean {
  return (INDEXABLE_STATIC_PATHS as readonly string[]).includes(normalizeSeoPath(pathOrUrl));
}

/**
 * Default robots policy for a route. Everything that is not an explicitly
 * indexable static surface is noindex,follow — including Lens pages, which
 * only become indexable when the page itself proves the Lens is published.
 */
export function resolveRouteRobots(pathOrUrl: string): RobotsDirective {
  return isDefaultIndexablePath(pathOrUrl) ? INDEX_ROBOTS : NOINDEX_ROBOTS;
}

export function isIndexableLensStatus(status: string | null | undefined): boolean {
  return status === PUBLISHED_LENS_STATUS;
}

export interface SeoTagOptions {
  path: string;
  title?: string;
  description?: string;
  robots?: RobotsDirective;
  canonical?: string;
  image?: string;
  ogType?: string;
  twitterCard?: string;
}

export interface SeoTags {
  title: string;
  canonical: string;
  metaNames: Record<string, string>;
  metaProperties: Record<string, string>;
}

/**
 * Always returns the complete metadata set, so applying it on a route change
 * overwrites every value the previous route wrote (no stale robots/canonical).
 */
export function computeSeoTags(options: SeoTagOptions): SeoTags {
  const title = options.title?.trim() || DEFAULT_SEO_TITLE;
  const description = options.description?.trim() || DEFAULT_SEO_DESCRIPTION;
  const robots = options.robots ?? resolveRouteRobots(options.path);
  const canonical = buildCanonicalUrl(options.canonical ?? options.path);
  const image = options.image ?? DEFAULT_OG_IMAGE;

  return {
    title,
    canonical,
    metaNames: {
      description,
      robots,
      'twitter:card': options.twitterCard ?? 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': image,
    },
    metaProperties: {
      'og:title': title,
      'og:description': description,
      'og:type': options.ogType ?? 'website',
      'og:url': canonical,
      'og:image': image,
    },
  };
}

export interface LensSeoSource {
  name: string;
  author?: string | null;
  shortDescription?: string | null;
  bio?: string | null;
  industry?: string | null;
}

/** Title includes the method and expert name, because those are searched directly. */
export function buildLensSeoTitle(name: string, author?: string | null): string {
  const method = name?.trim() || 'Expert Lens';
  const expert = author?.trim();
  return expert ? `${method} by ${expert} | Applyt` : `${method} | Applyt`;
}

/** Uses only Lens data already visible on the page; never invents a description. */
export function buildLensSeoDescription(source: LensSeoSource): string {
  const method = source.name?.trim() || 'this Expert Lens';
  const expert = source.author?.trim();
  const detail = source.shortDescription?.trim() || source.bio?.trim();
  if (detail) {
    return expert ? `${method} by ${expert}. ${detail}` : `${method}. ${detail}`;
  }
  const industry = source.industry?.trim();
  const lead = expert ? `Apply the ${method} method by ${expert}` : `Apply the ${method} method`;
  return industry
    ? `${lead} to your own ${industry} situation on Applyt.`
    : `${lead} to your own situation on Applyt.`;
}
